import { EvalModel } from '@/types/evals-types';

export const LLMODELS = {
  Llama: 'cerebras-llama-3.3-70b',
  GPT4Mini: 'gpt_4_omni_mini_128k',
  GPT4: 'gpt_4_omni_128k',
  GPT5: 'gpt_5',
  GPT5mini: 'gpt_5_mini',
  Claude4: 'claude-sonnet-4-20250514',
  Claude45: 'claude-sonnet-4-5-20250929',
} as const;

export const SQL_LLMODELS = {
  GPT4Mini: 'gpt-4o-mini',
  GPT4: 'gpt-4o',
  GPT5Mini: 'gpt-5-mini',
  GPT5: 'gpt-5',
  Claude4: 'claude-sonnet-4.0',
  Claude45: 'claude-sonnet-4.5',
} as const;

export type LLMModel = (typeof LLMODELS)[keyof typeof LLMODELS];
export type SQLLLMModel = (typeof SQL_LLMODELS)[keyof typeof SQL_LLMODELS];

export const MODELS_LABELS = {
  GPT4Mini: 'GPT-4o Mini',
  GPT4: 'GPT-4o',
  GPT5: 'GPT-5',
  GPT5mini: 'GPT-5 Mini',
  Llama: 'Llama 3.3 70B',
  GptOss120B: 'GPT-OSS 120B',
  GPT54: 'GPT-5.4',
  Claude4: 'Claude Sonnet 4',
  Claude45: 'Claude Sonnet 4.5',
  ClaudeOpus46: 'Claude Opus 4.6',
  ClaudeSonnet46: 'Claude Sonnet 4.6',
} as const;

export const MODELS: Array<{ label: string; value: LLMModel }> = [
  {
    label: MODELS_LABELS.GPT4Mini,
    value: LLMODELS.GPT4Mini,
  },
  {
    label: MODELS_LABELS.GPT4,
    value: LLMODELS.GPT4,
  },
  {
    label: MODELS_LABELS.GPT5,
    value: LLMODELS.GPT5,
  },
  {
    label: MODELS_LABELS.GPT5mini,
    value: LLMODELS.GPT5mini,
  },
  {
    label: MODELS_LABELS.Llama,
    value: LLMODELS.Llama,
  },
  {
    label: MODELS_LABELS.Claude4,
    value: LLMODELS.Claude4,
  },
  {
    label: MODELS_LABELS.Claude45,
    value: LLMODELS.Claude45,
  },
];

export const SQL_MODELS: Array<{ label: string; value: SQLLLMModel }> = [
  {
    label: MODELS_LABELS.GPT4Mini,
    value: SQL_LLMODELS.GPT4Mini,
  },
  {
    label: MODELS_LABELS.GPT4,
    value: SQL_LLMODELS.GPT4,
  },
  {
    label: MODELS_LABELS.GPT5,
    value: SQL_LLMODELS.GPT5,
  },
  {
    label: MODELS_LABELS.GPT5mini,
    value: SQL_LLMODELS.GPT5Mini,
  },
  {
    label: MODELS_LABELS.Claude4,
    value: SQL_LLMODELS.Claude4,
  },
  {
    label: MODELS_LABELS.Claude45,
    value: SQL_LLMODELS.Claude45,
  },
];

/**
 * Temporary fix: Maps deprecated Claude model versions to Claude 4.5
 * @param model - The model value from the backend
 * @returns The normalized model value (Claude 4.5 if deprecated, otherwise original)
 */
export const normalizeDeprecatedModel = (model: string): LLMModel => {
  // Map deprecated Claude models to Claude 4.5
  if (
    model === 'claude-3-5-sonnet-20240620' ||
    model === 'claude-3-7-sonnet-20250219' ||
    model === 'claude-sonnet-4-5' // Legacy Claude 4.5 without date suffix
  ) {
    return LLMODELS.Claude45;
  }
  return model as LLMModel;
};

/**
 * Temporary fix: Maps deprecated Claude SQL model versions to Claude 4.5
 * @param model - The SQL model value from the backend
 * @returns The normalized SQL model value (Claude 4.5 if deprecated, otherwise original)
 */
export const normalizeDeprecatedSQLModel = (model: string): SQLLLMModel => {
  // Map deprecated Claude models to Claude 4.5
  if (model === 'claude-sonnet-3.5' || model === 'claude-sonnet-3.7') {
    return SQL_LLMODELS.Claude45;
  }
  return model as SQLLLMModel;
};

/**
 * Temporary fix: Maps deprecated Claude model versions to the fact-check model format (claude-sonnet-4.5)
 * This is different from normalizeDeprecatedModel because fact-check models use a different value format
 * @param model - The model value from the backend (could be LLM model or fact-check model format)
 * @returns The normalized fact-check model value (claude-sonnet-4.5 if deprecated, otherwise original)
 */
export const normalizeDeprecatedFactCheckModel = (
  model: string | undefined,
): string => {
  if (!model) return '';

  // Map deprecated Claude 3.5 and 3.7 models to Claude 4.5
  if (model === 'claude-sonnet-3.5' || model === 'claude-sonnet-3.7') {
    return 'claude-sonnet-4.5';
  }
  // Map deprecated Llama model to GPT-OSS 120B
  if (model === 'llama-3.3-70b') {
    return 'gpt-oss-120b';
  }
  return model;
};

/**
 * Temporary fix: Maps deprecated evaluation model versions to valid EvalModel values
 * Handles deprecated Claude models (3.5, 3.7) and maps them to Claude Sonnet 4.5
 * @param model - The model value from the backend (could be deprecated)
 * @returns The normalized EvalModel value (Claude 4.5 if deprecated, otherwise original)
 */
export const normalizeDeprecatedEvalModel = (
  model: string | undefined,
): string => {
  if (!model) return EvalModel.CLAUDE_SONNET_46;

  // Map deprecated Claude 3.5 and 3.7 models to Claude Sonnet 4.6
  if (
    model === 'claude-sonnet-3.5' ||
    model === 'claude-sonnet-3.7' ||
    model === 'claude-3-5-sonnet-20240620' ||
    model === 'claude-3-7-sonnet-20250219' ||
    model === 'cerebras-llama-3.3-70b'
  ) {
    return EvalModel.CLAUDE_SONNET_46;
  }

  return model;
};
